import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { EventDocumentType } from '@application/enums/event-document-type';
import { PriorityEnum } from '@application/enums/priority-enum';
import { TaskCompletionStatusEnum } from '@application/enums/task-completion-status-enum';
import { getTasksByEventIdGroupedByStatusAsync } from '@application/features/task/taskThunk';
import { getVDKEventByIdAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { formatTaskDate } from '@application/utilities/date-helper';
import { Box, Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import TaskCard from '@ui/components/Cards/TaskCard';
import { StyledMenu } from '@ui/components/common/Menu';
import AddNewTask from '@ui/pages/Tasks/AddNewTask';
import { ArrowRight, Task } from 'iconsax-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import useTasksStyles from './useTasksStyles';
import { hasPermission } from '@application/helpers/permission-helper';
import { useSelector } from 'react-redux';
import { PageLayout } from '@ui/components/PageLayout';
import { colCenter, jEnd } from '@ui/style/style';
import { Icon } from '@iconify/react';
import BDrawer from '@ui/components/common/BDrawer';
import AddNewTaskDialog from '@ui/pages/Tasks/AddNewTask/addNewTaskDialog';
import { useRedirectOnCompanyChange } from '@ui/hooks/useRedirectOnCompanyChange';
const EventTasks = () => {
    useRedirectOnCompanyChange('/events');
    const theme = useTheme();
    const { t } = useTranslation();
    const styles = useTasksStyles();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { eventId } = useParams();
    const vdkEventState = useAppSelector((state) => state.VDKEvent);
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canCreateTasks = hasPermission(loggedInUserPermissions, 'Permissions.VdkTasks.Create');
    useEffect(() => {
        if (eventId) {
            dispatch(getVDKEventByIdAsync({
                id: eventId,
                documentType: EventDocumentType.BackgroundImage
            }));
            dispatch(getTasksByEventIdGroupedByStatusAsync({ eventId }));
        }
    }, [dispatch, eventId]);
    const handleTaskStatusUpdate = () => {
        if (eventId) {
            dispatch(getTasksByEventIdGroupedByStatusAsync({ eventId }));
        }
    };
    const handleTaskCreated = (createdEventId) => {
        if (createdEventId) {
            dispatch(getTasksByEventIdGroupedByStatusAsync({ eventId: createdEventId }));
        }
    };
    const [dialogOpen, setDialogOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { tasksByEventIdGroupedByStatus } = useAppSelector((state) => state.task);
    // Group tasks by completion status
    const newTasks = tasksByEventIdGroupedByStatus?.find((group) => group.completionStatus == TaskCompletionStatusEnum.New)?.tasks || [];
    const inProgressTasks = tasksByEventIdGroupedByStatus?.find((group) => group.completionStatus == TaskCompletionStatusEnum.InProgress)?.tasks || [];
    const completedTasks = tasksByEventIdGroupedByStatus?.find((group) => group.completionStatus == TaskCompletionStatusEnum.Completed)?.tasks || [];
    // Helper function to map priority enum to TaskCard priority
    const mapPriorityToTaskCard = (priority) => {
        if (priority === undefined || priority === null) {
            return PriorityEnum.Medium;
        }
        switch (priority) {
            // case PriorityEnum.Critical:
            case PriorityEnum.High:
                return PriorityEnum.High;
            case PriorityEnum.Medium:
                return PriorityEnum.Medium;
            case PriorityEnum.Low:
                // case PriorityEnum.Trivial:
                return PriorityEnum.Low;
            case PriorityEnum.None:
            default:
                return PriorityEnum.Medium; // Default to medium for None or any unhandled cases
        }
    };
    const handleMoreClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };
    // Replace static heading with dynamic event title
    const eventTitle = vdkEventState.VDKEvent?.title || t('common.event');
    return (_jsxs(PageLayout, { title: `${eventTitle}`, secondTitle: t('tasks.taskManagerSubtitle'), leftIcon: _jsx(Task, {}), rightContent: !isSmallScreen ? (_jsxs(Box, { sx: { ...styles.taskHeaderButtonContainer }, children: [_jsx(Button, { variant: 'white', size: 'large', color: 'inherit', onClick: () => navigate('/'), children: t('common.home') }), _jsx(Button, { variant: 'white', size: 'large', color: 'inherit', onClick: () => navigate('/create-event'), children: t('actions.createNewEvent') }), _jsx(Button, { size: 'large', variant: 'contained', endIcon: _jsx(ArrowRight, { fontSize: 'large' }), onClick: () => navigate(`/event-details/${eventId}`), children: t('eventSuccess.eventDetails') })] })) : (_jsxs(Box, { sx: { ...colCenter, gap: 0.5 }, children: [_jsx(IconButton, { size: 'small', onClick: handleMoreClick, children: _jsx(Icon, { icon: 'solar:menu-dots-bold', color: theme.palette.common.white }) }), _jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), anchorOrigin: { vertical: 'top', horizontal: 'center' }, transformOrigin: { vertical: 'bottom', horizontal: 'center' }, onClose: handleMenuClose, actions: [
                        {
                            text: t('common.home'),
                            to: () => {
                                navigate('/');
                                handleMenuClose();
                            }
                        },
                        {
                            text: t('actions.createNewEvent'),
                            to: () => {
                                navigate('/create-event');
                                handleMenuClose();
                            }
                        },
                        {
                            text: t('eventSuccess.eventDetails'),
                            to: () => {
                                navigate(`/event-details/${eventId}`);
                                handleMenuClose();
                            }
                        }
                    ] }), _jsx(Button, { variant: 'contained', color: 'primary', size: 'small', onClick: () => setDrawerOpen(true), children: t('actions.newTask') })] })), children: [_jsx(Box, { children: _jsxs(Grid, { container: true, children: [isSmallScreen ? null : (_jsx(Grid, { size: 12, children: _jsx(Box, { sx: { ...jEnd, pb: '12px' }, children: _jsx(Button, { startIcon: _jsx(Task, { size: 16 }), variant: 'contained', color: 'primary', size: 'large', onClick: () => setDialogOpen(true), children: t('actions.newTask') }) }) })), _jsx(Grid, { size: { xs: 12, sm: 12, lg: 12 }, children: _jsxs(Box, { sx: { maxHeight: { xs: 'auto', lg: 'calc(100dvh - 420px)' } }, children: [_jsx(Grid, { container: true, spacing: 1.5, children: newTasks
                                            .concat(inProgressTasks, completedTasks)
                                            .filter((task) => task.eventId === eventId)
                                            .map((task) => {
                                            const taskDate = formatTaskDate(task.deadline);
                                            return (_jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(TaskCard, { heading: task.name, eventOrganizer: eventTitle, date: taskDate.date, showRating: false, time: taskDate.time, priority: mapPriorityToTaskCard(task.priority), status: task.completionStatus, taskData: task, onStatusUpdate: handleTaskStatusUpdate, userAssignedDisabled: task.completionStatus == TaskCompletionStatusEnum.Completed
                                                        ? true
                                                        : false }, task.id) }, task.id));
                                        }) }), _jsx(Box, { sx: { display: 'none', visibility: 'hidden' }, children: _jsx(AddNewTask, { padding: true, taskTab: true, isCreateAllowed: canCreateTasks, mt: true, height: true }) })] }) })] }) }), _jsx(BDrawer, { anchor: 'bottom', open: drawerOpen, title: t('tasks.addNewTask'), onClose: () => setDrawerOpen(false), children: _jsx(AddNewTask, { isDrawer: true, isCreateAllowed: canCreateTasks }) }), _jsx(AddNewTaskDialog, { open: dialogOpen, onClose: () => setDialogOpen(false), isCreateAllowed: canCreateTasks, onTaskCreated: handleTaskCreated })] }));
};
export default EventTasks;
